import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack
} from '@mui/material';
import {
    Group as GroupIcon,
    CheckCircle as ActiveIcon,
    Discount as DiscountIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import CustomerList from './customers/CustomerList';
import { customerService } from '../../services/customerService';

const CustomerManagement = () => {
    const [stats, setStats] = useState(null);
    const { showSuccess, showError } = useApiHandler();

    useEffect(() => {
        loadCustomerStats();
    }, []);

    const loadCustomerStats = async () => {
        try {
            const response = await customerService.getCustomerStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            showError('Error loading customer statistics');
        }
    };

    const refreshStats = () => {
        loadCustomerStats();
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Customer Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage customer accounts, their assigned packages and business relationships
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <GroupIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Customers</Typography>
                                </Box>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    {stats.total_customers}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={`${stats.active_customers} Active`} 
                                        color="success" 
                                        size="small" 
                                    />
                                    <Chip 
                                        label={`${stats.inactive_customers} Inactive`} 
                                        color="default" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ActiveIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Active Customers</Typography>
                                </Box>
                                <Typography variant="h4" color="success.main" gutterBottom>
                                    {stats.active_customers}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="Account Active" 
                                        color="success" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <DiscountIcon color="info" sx={{ mr: 1 }} />
                                    <Typography variant="h6">With Assigned Packages</Typography>
                                </Box>
                                <Typography variant="h4" color="info.main" gutterBottom>
                                    {stats.customers_with_discounts}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {/* The rest are unrestricted, which is the more
                                        useful number to state explicitly. */}
                                    <Chip
                                        label={`${Math.max((stats.total_customers || 0) - (stats.customers_with_discounts || 0), 0)} can book any package`}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Customer Management */}
            <Paper sx={{ width: '100%' }}>
                <CustomerList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
};

export default CustomerManagement; 